import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useEffect } from "react";

export interface MessagingData {
  conversationId: string;
  otherUserId: string;
  unreadCount: number;
  lastUnreadMessage: string | null;
  lastUnreadAt: string | null;
}

/**
 * Master hook that fetches ALL messaging data in a single optimized query.
 * Other messaging hooks derive their data from this hook's cache.
 * Replaces N+1 query pattern with a single database function call.
 */
export function useMessagingData() {
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['messaging-data', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .rpc('get_user_messaging_data', { p_user_id: user.id });

      if (error) throw error;

      return (data || []).map((item: any) => ({
        conversationId: item.conversation_id,
        otherUserId: item.other_user_id,
        unreadCount: Number(item.unread_count || 0),
        lastUnreadMessage: item.last_unread_message,
        lastUnreadAt: item.last_unread_at,
      })) as MessagingData[];
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Real-time subscription for automatic updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('messaging-data-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'direct_messages',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['messaging-data', user.id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversation_participants',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['messaging-data', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return query;
}
